'use client';

import { Box, HStack, VStack, css } from '@terra/ui-styled-system';
import { useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  AlertCircleIcon,
  Alert01Icon,
  ChartHistogramIcon,
  Bug01Icon,
  ArrowDown01Icon,
  ArrowRight01Icon,
  Copy01Icon,
  File01Icon,
  InformationCircleIcon,
  Shield01Icon,
  Target03Icon,
  Wrench01Icon,
} from '@terra/ui-icons';
import { Text } from '../../../components/styled/text';
import {
  Badge,
  BadgeSeverity,
  isBadgeSeverityType,
} from '../../../components/badge';
import { IconButton } from '../../../components/icon-button';
import { Tooltip } from '../../../components/tooltip';
import { Collapsible } from '../../../components/collapsible';
import type { FindingMentionData } from '@terra/shared-dto-chat-types';
import { normalizeSeverityLabel } from '../../../utils/severity-label.utils';

type FindingMentionCardProps = {
  data: FindingMentionData;
  compact?: boolean;
};
const cardStyles = css({
  borderRadius: 'md',
  borderWidth: '1px',
  borderColor: 'border.muted',
  bg: 'bg.subtle',
  overflow: 'hidden',
  fontSize: 'xs',
});
const headerStyles = css({
  px: 3,
  py: 2,
  bg: 'bg.muted',
  borderBottomWidth: '1px',
  borderColor: 'border.muted',
  cursor: 'pointer',
  _hover: {
    bg: 'bg.hover',
  },
});
const sectionStyles = css({
  fontFamily: 'mono',
  fontSize: '2xs',
  bg: 'bg.canvas',
  p: 2,
  borderRadius: 'sm',
  overflow: 'auto',
  maxHeight: '200px',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-all',
});

function getSeverityIcon(severity?: string) {
  if (!severity) return <Bug01Icon size={12} />;
  const sev = severity.toLowerCase();

  if (sev === 'critical' || sev === 'high')
    return <AlertCircleIcon size={12} />;
  if (sev === 'medium') return <Alert01Icon size={12} />;

  return <InformationCircleIcon size={12} />;
}

function SeverityBadge({ severity }: { severity: string }) {
  const severityLabel = normalizeSeverityLabel(severity);
  const icon = getSeverityIcon(severity);

  if (isBadgeSeverityType(severityLabel)) {
    return (
      <BadgeSeverity
        size="sm"
        variant="outline"
        severity={severityLabel}
        icon={icon}
      >
        {severity}
      </BadgeSeverity>
    );
  }

  return (
    <Badge size="sm" variant="outline" icon={icon}>
      {severity}
    </Badge>
  );
}

function DetailSection({
  icon,
  label,
  value,
  compact,
  onCopy,
}: {
  icon: React.ReactNode;
  label: string;
  value?: string;
  compact?: boolean;
  onCopy: (text: string, section: string) => void;
}) {
  if (!value) return null;
  const displayValue =
    compact && value.length > 200 ? value.slice(0, 200) + '...' : value;

  return (
    <Box>
      <HStack justifyContent="space-between" mb={1}>
        <HStack gap={1} alignItems="center" color="fg.muted">
          {icon}
          <Text fontSize="2xs" fontWeight="medium" color="fg.muted">
            {label}
          </Text>
        </HStack>
        <Tooltip title={`Copy ${label.toLowerCase()}`}>
          <IconButton
            size="xs"
            variant="ghost"
            aria-label={`Copy ${label.toLowerCase()}`}
            onClick={() => onCopy(value, label.toLowerCase())}
          >
            <Copy01Icon size={10} />
          </IconButton>
        </Tooltip>
      </HStack>
      <Box className={sectionStyles}>
        <Text fontSize="2xs" color="fg.default" whiteSpace="pre-wrap">
          {displayValue}
        </Text>
      </Box>
    </Box>
  );
}

export function FindingMentionCard({
  data,
  compact = false,
}: FindingMentionCardProps) {
  const tFinding = useTranslations('designSystem.findings.findingDrawer');
  const tTable = useTranslations('designSystem.findingsList.table');
  const tCard = useTranslations('designSystem.chat.findingMentionCard');
  const [isExpanded, setIsExpanded] = useState(false);
  const [copiedSection, setCopiedSection] = useState<string | null>(null);
  const handleCopy = async (text: string, section: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedSection(section);
      setTimeout(() => setCopiedSection(null), 1500);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const handleCopyAll = () => {
    handleCopy(data.rawText, 'all');
  };

  const hasExpandableContent =
    data.description || data.poc || data.businessImpact || data.mitigationPlan;

  return (
    <Box className={cardStyles}>
      {/* Header - always visible */}
      <HStack
        className={headerStyles}
        justifyContent="space-between"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <HStack gap={2} flex={1} minW={0}>
          {/* Expand/collapse icon */}
          {hasExpandableContent && (
            <Box color="fg.muted" flexShrink={0}>
              {isExpanded ? (
                <ArrowDown01Icon size={14} />
              ) : (
                <ArrowRight01Icon size={14} />
              )}
            </Box>
          )}

          {/* Finding type badge */}
          <Badge
            size="sm"
            variant="fill"
            leadingContent={<Bug01Icon size={10} />}
          >
            {tFinding('entityTitle')}
          </Badge>

          {/* Severity badge */}
          {data.severity ? <SeverityBadge severity={data.severity} /> : null}

          {/* Category badge */}
          {data.category && (
            <Badge size="sm" variant="emphasis">
              {data.category.replace(/_/g, ' ')}
            </Badge>
          )}

          {/* Title */}
          <Text
            fontSize="xs"
            color="fg.default"
            truncate
            flex={1}
            fontWeight="medium"
          >
            {data.title}
          </Text>
        </HStack>

        {/* Quick actions */}
        <HStack gap={1} flexShrink={0} onClick={(e) => e.stopPropagation()}>
          <Tooltip title={copiedSection === 'all' ? 'Copied!' : 'Copy all'}>
            <IconButton
              size="xs"
              variant="ghost"
              aria-label="Copy all"
              onClick={handleCopyAll}
            >
              <Copy01Icon size={12} />
            </IconButton>
          </Tooltip>
        </HStack>
      </HStack>

      {/* Expanded content */}
      {hasExpandableContent && (
        <Collapsible.Root open={isExpanded}>
          <Collapsible.Content>
            <VStack gap={2} p={3} alignItems="stretch">
              {/* Meta info row */}
              <HStack gap={4} flexWrap="wrap">
                {data.status && (
                  <Box>
                    <Text fontSize="2xs" fontWeight="medium" color="fg.muted">
                      {tTable('status')}
                    </Text>
                    <Text fontSize="xs" color="fg.default">
                      {data.status}
                    </Text>
                  </Box>
                )}
                {data.cvssScore && (
                  <Box>
                    <Text fontSize="2xs" fontWeight="medium" color="fg.muted">
                      CVSS
                    </Text>
                    <HStack gap={1} alignItems="center">
                      <ChartHistogramIcon size={10} />
                      <Text fontSize="xs" fontFamily="mono" color="fg.default">
                        {data.cvssScore}
                      </Text>
                    </HStack>
                  </Box>
                )}
                {data.affectedAssets && data.affectedAssets.length > 0 && (
                  <Box>
                    <Text fontSize="2xs" fontWeight="medium" color="fg.muted">
                      {tTable('assets')}
                    </Text>
                    <Text fontSize="xs" fontFamily="mono" color="fg.default">
                      {tCard('affectedCount', {
                        count: data.affectedAssets.length,
                      })}
                    </Text>
                  </Box>
                )}
              </HStack>

              {/* Short description */}
              {data.shortDescription && (
                <Box>
                  <Text
                    fontSize="xs"
                    color="fg.muted"
                    lineClamp={compact ? 2 : undefined}
                  >
                    {data.shortDescription}
                  </Text>
                </Box>
              )}

              {/* Detailed sections */}
              <DetailSection
                icon={<File01Icon size={12} />}
                label="Description"
                value={data.description}
                compact={compact}
                onCopy={handleCopy}
              />

              <DetailSection
                icon={<Target03Icon size={12} />}
                label="Business Impact"
                value={data.businessImpact}
                compact={compact}
                onCopy={handleCopy}
              />

              <DetailSection
                icon={<Shield01Icon size={12} />}
                label="Proof of Concept"
                value={data.poc}
                compact={compact}
                onCopy={handleCopy}
              />

              <DetailSection
                icon={<Wrench01Icon size={12} />}
                label="Mitigation Plan"
                value={data.mitigationPlan}
                compact={compact}
                onCopy={handleCopy}
              />

              {/* Affected assets list */}
              {data.affectedAssets && data.affectedAssets.length > 0 && (
                <Box>
                  <Text
                    fontSize="2xs"
                    fontWeight="medium"
                    color="fg.muted"
                    mb={1}
                  >
                    {tCard('affectedAssets')}
                  </Text>
                  <Box className={sectionStyles}>
                    {data.affectedAssets
                      .slice(0, compact ? 3 : undefined)
                      .map((asset) => (
                        <Text
                          key={asset}
                          fontSize="2xs"
                          color="fg.default"
                          fontFamily="mono"
                        >
                          {asset}
                        </Text>
                      ))}
                    {compact && data.affectedAssets.length > 3 && (
                      <Text color="fg.muted" fontStyle="italic" fontSize="2xs">
                        {tCard('andMore', {
                          count: data.affectedAssets.length - 3,
                        })}
                      </Text>
                    )}
                  </Box>
                </Box>
              )}
            </VStack>
          </Collapsible.Content>
        </Collapsible.Root>
      )}
    </Box>
  );
}
